import { createAsyncThunk } from "@reduxjs/toolkit";
import client, {
  ApiError,
  AsyncThunkConfig,
  isAxiosError,
} from "../../api/axiosConfig";
import { dispatchNotification } from "../../utils/redux";
import { FileData } from "../../api/psychologicalLabApi";
import { registryURL } from "../../utils/paths";
import { getTimezoneOffset } from "../../utils/timezone";
import { RegistryFilters, RegistryFiltersData } from "../../api/registryApi";

export const loadRegistryFiltersData = createAsyncThunk<
  RegistryFiltersData,
  number | null | undefined,
  AsyncThunkConfig
>("registry/loadRegistryFiltersData/get", async (labId, thunkAPI) => {
  try {
    const params: Record<string, string> = {};

    if (labId != null && labId > 0) {
      // eslint-disable-next-line camelcase -- API query key
      params.lab_id = String(labId);
    }
    const response = await client.request<{ results: RegistryFiltersData }>({
      url: `${registryURL}filters/`,
      params,
    });

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message, true);

      return thunkAPI.rejectWithValue(extractedError);
    }
    dispatchNotification("error");
    throw err;
  }
});

export const fetchRegistry = createAsyncThunk<
  void,
  RegistryFilters,
  AsyncThunkConfig
>("registry/fetchRegistry", async (filters, thunkAPI) => {
  try {
    await client.post(`${registryURL}generate/`, filters, {
      headers: {
        "Content-Type": "application/json",
        "X-Timezone-Offset": getTimezoneOffset().toString(),
      },
    });

    return;
  } catch (err) {
    if (isAxiosError(err) && err.response?.data) {
      const errorData = err.response.data as ApiError;
      const errorMessage =
        errorData.message ||
        "Wystąpił nieznany błąd podczas generowania rejestru.";

      dispatchNotification("error", errorMessage, true);

      return thunkAPI.rejectWithValue(errorData);
    }

    throw err;
  }
});

interface FetchRegistryFilesApiResponse {
  results: {
    files: FileData[];
  };
}

export const fetchRegistryFiles = createAsyncThunk<
  FileData[],
  boolean | undefined,
  AsyncThunkConfig
>("registry/fetchRegistryFiles", async (fromAdmin, thunkAPI) => {
  try {
    const params: Record<string, string> = {};

    if (fromAdmin) {
      // eslint-disable-next-line camelcase -- API query key
      params.from_admin = "true";
    }
    const response = await client.get<FetchRegistryFilesApiResponse>(
      `${registryURL}fetchfiles/`,
      { params }
    );

    return response.data.results.files;
  } catch (err) {
    let errorMessage =
      "Wystąpił błąd podczas pobierania listy plików rejestru.";
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (isAxiosError(err) && err.response?.data) {
      apiError = err.response.data as ApiError;
      errorMessage = apiError.message || errorMessage;
      dispatchNotification("error", errorMessage, true);

      return thunkAPI.rejectWithValue(apiError);
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});

export const deleteRegistryFile = createAsyncThunk<
  string,
  string,
  AsyncThunkConfig
>("registry/deleteRegistryFile", async (filename, thunkAPI) => {
  try {
    await client.delete(
      `${registryURL}delete/${encodeURIComponent(filename)}/`
    );

    return filename;
  } catch (err) {
    let errorMessage = `Wystąpił błąd podczas usuwania pliku: ${filename}.`;
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (isAxiosError(err) && err.response?.data) {
      try {
        if (
          typeof err.response.data === "object" &&
          !(err.response.data instanceof Blob)
        ) {
          apiError = err.response.data as ApiError;
          errorMessage = apiError.message || errorMessage;
        }
      } catch {
        // Error processing Axios error data
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});

export const downloadRegistryFile = createAsyncThunk<
  void,
  string,
  AsyncThunkConfig
>("registry/downloadRegistryFile", async (filename, thunkAPI) => {
  try {
    const response = await client.get(
      `${registryURL}download/${encodeURIComponent(filename)}/`,
      {
        responseType: "blob",
      }
    );

    if (typeof window !== "undefined") {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = /^(https?:\/\/|blob:)/.test(url) ? url : "#";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    return;
  } catch (err) {
    let errorMessage = `Wystąpił błąd podczas pobierania pliku: ${filename}.`;
    let apiError: ApiError = {
      message: errorMessage,
      results: null,
      error: true,
    };

    if (
      isAxiosError(err) &&
      err.response?.data instanceof Blob &&
      err.response.data.type === "application/json"
    ) {
      try {
        const errorJson = JSON.parse(
          await err.response.data.text()
        ) as ApiError;

        apiError = errorJson;
        errorMessage = apiError.message || errorMessage;
      } catch {
        // Failed to parse Blob error as JSON
      }
    } else if (isAxiosError(err) && err.response?.data) {
      try {
        if (
          typeof err.response.data === "object" &&
          !(err.response.data instanceof Blob)
        ) {
          apiError = err.response.data as ApiError;
          errorMessage = apiError.message || errorMessage;
        }
      } catch {
        // Error processing Axios error data
      }
    } else if (err instanceof Error) {
      errorMessage = err.message;
      apiError.message = errorMessage;
    }

    dispatchNotification("error", errorMessage, true);

    return thunkAPI.rejectWithValue(apiError);
  }
});